import type { QueryStatus, UseQueryResult, QueryObserverOptions } from '@tanstack/react-query';

import type { ApiResponse } from '@openclassrooms/sdk-web';

import type { Permission } from '../interfaces';
import getUseGet from './getUseGet';
import checkPermission from './checkPermission';

type GetPermissionsHookParams = {
  entityName: string;
  apiResponseRootKey?: string;
  fetch: (params?: unknown) => Promise<ApiResponse>;
};

type GetPermissionsHookReturn<
  PermissionCodes extends string,
  HookParams = unknown,
  HookOptions = QueryObserverOptions
> = (
  params?: HookParams,
  options?: HookOptions
) => {
  isFetching: boolean;
  initialStatus: QueryStatus;
  hasError: boolean;
  can: (permissionCode: PermissionCodes) => boolean;
} & Omit<UseQueryResult<Permission[]>, 'data' | 'error' | 'status'>;

export default function getPermissionsHook<
  PermissionCodes extends string,
  HookParams = unknown,
  HookOptions = QueryObserverOptions
>({
  entityName,
  fetch,
  apiResponseRootKey,
}: GetPermissionsHookParams): GetPermissionsHookReturn<PermissionCodes, HookParams, HookOptions> {
  const usePermissions = getUseGet<unknown, Permission[], unknown>(fetch, entityName);

  return (params?: HookParams, options?: HookOptions) => {
    const {
      isFetching,
      data,
      error,
      status: initialStatus,
      ...queryMetadata
    } = usePermissions(params, options) as ReturnType<typeof usePermissions> & {
      data: Record<string, Array<Permission>>;
    };

    const key = apiResponseRootKey || 'permissions';
    const permissions: Array<Permission> = data ? (data[key] as Array<Permission>) : [];
    const hasError = Boolean(error);

    const can = (permissionCode: PermissionCodes) => checkPermission(permissionCode, permissions);

    return {
      isFetching,
      hasError,
      can,
      initialStatus,
      ...queryMetadata,
    };
  };
}
